/**
 * HighTechActionButton — Premium glassmorphic action button
 * 
 * Part of the YUL On Tap High-Tech Interaction Design System.
 * Dark translucent glass surface with contextual colored accent,
 * high-tech icon, strong title, optional description, and chevron.
 */
import React from 'react';
import { ChevronRight } from 'lucide-react';

export default function HighTechActionButton({
  icon: Icon,
  title,
  description,
  accent,
  onClick,
  disabled = false,
  badge,
  testId,
}) {
  const accentColor = accent?.color || '#64748b';
  const accentGlow = accent?.glow || 'rgba(100,116,139,0.3)';
  const accentBorder = accent?.border || 'rgba(100,116,139,0.4)';
  const accentBg = accent?.bg || 'rgba(100,116,139,0.08)';

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      className={`
        group w-full flex items-center gap-4 p-4 rounded-2xl
        transition-all duration-200 ease-out
        border text-left
        ${disabled
          ? 'opacity-40 cursor-not-allowed'
          : 'cursor-pointer active:scale-[0.98]'
        }
      `}
      style={{
        background: `linear-gradient(135deg, rgba(15,23,42,0.85), rgba(15,23,42,0.65))`,
        borderColor: accentBorder,
        boxShadow: disabled ? 'none' : `0 0 20px ${accentBg}, inset 0 1px 0 rgba(255,255,255,0.05)`,
      }}
      onMouseEnter={(e) => {
        if (!disabled) {
          e.currentTarget.style.boxShadow = `0 0 30px ${accentGlow}, 0 4px 20px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.08)`;
          e.currentTarget.style.borderColor = accentColor;
        }
      }}
      onMouseLeave={(e) => {
        if (!disabled) {
          e.currentTarget.style.boxShadow = `0 0 20px ${accentBg}, inset 0 1px 0 rgba(255,255,255,0.05)`;
          e.currentTarget.style.borderColor = accentBorder;
        }
      }}
    >
      {/* Icon */}
      <div
        className="flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center transition-all duration-200"
        style={{
          background: accentBg,
          border: `1px solid ${accentBorder}`,
          boxShadow: `0 0 12px ${accentBg}`,
        }}
      >
        {Icon && <Icon className="w-5 h-5" style={{ color: accentColor }} strokeWidth={1.5} />}
      </div>

      {/* Text */}
      <div className="flex-1 min-w-0">
        <p className="text-white font-semibold text-sm leading-tight group-hover:text-white/95">
          {title}
        </p>
        {description && (
          <p className="text-white/50 text-xs mt-0.5 leading-tight">
            {description}
          </p>
        )}
      </div>

      {/* Badge (optional) */}
      {badge && (
        <span
          className="flex-shrink-0 text-[10px] font-bold px-2 py-0.5 rounded-full"
          style={{ backgroundColor: accentBg, color: accentColor, border: `1px solid ${accentBorder}` }}
        >
          {badge}
        </span>
      )}

      {/* Chevron */}
      <ChevronRight
        className="flex-shrink-0 w-5 h-5 text-white/30 transition-all duration-200 group-hover:text-white/60 group-hover:translate-x-0.5"
        strokeWidth={1.5}
      />
    </button>
  );
}
